import { useCallback } from 'react';
import { v4 } from 'uuid';

import type { PackageType } from '@shipstation/client-common/types/seller-data';
import {
  MULTI_PACKAGE_ADD_PACKAGE_CLICKED,
  MULTI_PACKAGE_DUPLICATE_CLICKED,
  MULTI_PACKAGE_DELETE_CLICKED,
} from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import { convertToDimensionUnitEnum } from '../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { createDefaultPackage } from '../helpers/package-helpers';
import type { ItemQuantities } from '../helpers/assignment-helpers';
import type { Package } from '../types';
import type { Dimensions, Weight } from '../../multi-package-modal/package-set';

type UpdatePackageCallback = (
  setPackages: React.Dispatch<React.SetStateAction<Package[]>>,
  updateObj: Partial<Package>,
  index: number,
) => void;

const updatePackage: UpdatePackageCallback = (setPackages, updateObj, index) => {
  setPackages((packages) => {
    const updatedPackage = {
      ...packages[index],
      ...updateObj,
    };
    const copiedPackages = [...packages];
    copiedPackages[index] = updatedPackage;
    return copiedPackages;
  });
};

type UsePackageOperationsProps = {
  packages: Package[];
  setPackages: React.Dispatch<React.SetStateAction<Package[]>>;
  itemQuantities: ItemQuantities;
  unassignItemFromPackage: (itemId: string, chunkId: string) => void;
  reindexPackagesAfterRemoval: (removedPackageIndex: number) => void;
  sellerDefaultWeightUnit: string;
  sellerDefaultDimensionsUnit: string;
  sellerDefaultCurrencyCode: string;
};

export const usePackageOperations = ({
  packages,
  setPackages,
  itemQuantities,
  unassignItemFromPackage,
  reindexPackagesAfterRemoval,
  sellerDefaultWeightUnit,
  sellerDefaultDimensionsUnit,
  sellerDefaultCurrencyCode,
}: UsePackageOperationsProps) => {
  const addPackages = useCallback(
    (qtyToAdd: number) => {
      Segment.trackEvent(MULTI_PACKAGE_ADD_PACKAGE_CLICKED, {
        packagesAddedCount: qtyToAdd,
      });

      const packagesCurrency = packages[0]?.insuredValue?.code || sellerDefaultCurrencyCode;
      const newPackages = Array.from({ length: qtyToAdd }, () =>
        createDefaultPackage(sellerDefaultWeightUnit, sellerDefaultDimensionsUnit, packagesCurrency),
      );
      setPackages([...packages, ...newPackages]);
    },
    [packages, sellerDefaultCurrencyCode, sellerDefaultDimensionsUnit, sellerDefaultWeightUnit, setPackages],
  );

  const duplicatePackage = useCallback(
    (packageIndex: number) => {
      const { orderPackageId: _orderPackageId, ...packageWithoutOrderId } = packages[packageIndex];
      Segment.trackEvent(MULTI_PACKAGE_DUPLICATE_CLICKED, {
        packageCount: packages.length,
      });
      setPackages([
        ...packages,
        {
          ...packageWithoutOrderId,
          id: v4(),
        },
      ]);
    },
    [packages, setPackages],
  );

  const removePackage = useCallback(
    (packageIndex: number) => {
      Object.entries(itemQuantities).forEach(([itemId, quantities]) => {
        quantities.chunks.forEach((chunk) => {
          if (chunk.packageIndex === packageIndex) {
            unassignItemFromPackage(itemId, chunk.chunkId);
          }
        });
      });

      reindexPackagesAfterRemoval(packageIndex);

      Segment.trackEvent(MULTI_PACKAGE_DELETE_CLICKED, {
        packageCount: packages.length,
      });

      if (packages.length === 1) {
        const defaultPkg = createDefaultPackage(sellerDefaultWeightUnit, sellerDefaultDimensionsUnit, sellerDefaultCurrencyCode);
        setPackages([defaultPkg]);
      } else {
        setPackages(packages.filter((_, index) => index !== packageIndex));
      }
    },
    [
      packages,
      itemQuantities,
      unassignItemFromPackage,
      reindexPackagesAfterRemoval,
      sellerDefaultCurrencyCode,
      sellerDefaultDimensionsUnit,
      sellerDefaultWeightUnit,
      setPackages,
    ],
  );

  const changePackageDimensions = useCallback(
    (inputDimensions: Dimensions, index: number) => {
      const unit = convertToDimensionUnitEnum(inputDimensions.unit);
      const dimensions: Dimensions = {
        length: inputDimensions.length,
        width: inputDimensions.width,
        height: inputDimensions.height,
        unit,
      };
      updatePackage(setPackages, { dimensions }, index);
    },
    [setPackages],
  );

  const changeWeight = useCallback(
    (weightAndUnit: Weight, index: number) => {
      updatePackage(setPackages, { weight: weightAndUnit }, index);
    },
    [setPackages],
  );

  const changePackageContentDescription = useCallback(
    (updatedDescription: string, index: number) => {
      updatePackage(setPackages, { contentDescription: updatedDescription }, index);
    },
    [setPackages],
  );

  const changeInsuranceValue = useCallback(
    (insuredAmount: number, currencyCode: string, index: number) => {
      const parsedValue = Number.parseFloat(String(insuredAmount));
      const actualValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
      const insuredValue = {
        value: actualValue,
        code: currencyCode,
      };
      updatePackage(setPackages, { insuredValue }, index);
    },
    [setPackages],
  );

  const applyCustomPackageToPackage = useCallback(
    (customPackage: PackageType, packageIndex: number) => {
      updatePackage(
        setPackages,
        {
          dimensions: customPackage.dimensions || undefined,
          packageTypeName: customPackage.name,
        },
        packageIndex,
      );
    },
    [setPackages],
  );

  return {
    addPackages,
    duplicatePackage,
    removePackage,
    changePackageDimensions,
    changeWeight,
    changePackageContentDescription,
    changeInsuranceValue,
    applyCustomPackageToPackage,
  };
};
